import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Notify } from 'notiflix';
import { LocalstorageService } from './localstorage.service';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { RoutingService } from './routing.service';
import { SocketService } from './socket.service';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public user: any;
  public token: string | null;
  public ipCountry: string | null = null;

  constructor(
    private _apis: ApiService,
    private _ls: LocalstorageService,
    private _rs: RoutingService,
    private _sockets: SocketService,
  ) {
    this.token = this._ls.getAuthToken();
    // Initialize with default user for development
    this.user = {
      _id: 'user_123',
      name: 'Hector Clark',
      email: 'hector@example.com',
      role: 'admin',
      dob: '01-01-1990',
      profileImage: 'assets/images/profile.png',
      address: {
        country: 'USA',
      },
    };
  }

  public userGet() {
    if (!this.token) {
      // No token yet; keep default local user without hitting /api/user.
      this._sockets.setCurrentUser(this.user);
      this._sockets.emitAuth(this.token);
      return of(true);
    }

    return this._apis.user().pipe(
      map((data: any) => {
        if (data.data) {
          this.user = data.data;
          this.user.dob = this.user.dob
            ? moment(this.user.dob).format('DD-MM-YYYY')
            : '-';
          if (!this.user.country) {
            this.detectAndSaveCountry(this.user);
          }
          this._sockets.setCurrentUser(this.user);
          this._sockets.emitAuth(this.token);
          return true;
        } else {
          // Keep default user even if data is missing
          return true;
        }
      }),
      catchError((err: any) => {
        if (err?.status === 401) {
          console.info('Auth token invalid/expired, using default local user.');
        } else {
          console.warn('Failed to fetch user, using default user:', err);
        }
        // Continue with default user instead of failing
        this._sockets.setCurrentUser(this.user);
        this._sockets.emitAuth(this.token);
        return of(true);
      }),
    );
  }

  public login(data: any) {
    return this._apis.login(data).pipe(
      map((res: any) => {
        const token = res?.data?.token as string | undefined;
        if (!token || !res?.data) {
          Notify.failure('Login failed. Missing auth token.');
          return false;
        }

        this.user = res.data;
        this.token = token;
        if (!this.user.country) {
          this.detectAndSaveCountry(this.user);
        }
        this._sockets.setCurrentUser(this.user);
        this._ls.setAuthToken(token);
        this._sockets.emitAuth(this.token);
        return true;
      }),
      catchError((err: any) => {
        const errorMsg =
          err?.error?.error || 'Login failed. Invalid credentials.';
        Notify.failure(errorMsg);
        console.error('Login error:', err);
        return of(false);
      }),
    );
  }

  public register(data: any) {
    return this._apis.register(data).pipe(
      map((_res: any) => {
        return true;
      }),
      catchError((err: any) => {
        const errorMsg =
          err?.error?.error || 'Registration failed. Please try again.';
        Notify.failure(errorMsg);
        console.error('Register error:', err);
        return of(false);
      }),
    );
  }

  public logout(): void {
    if (this.user) {
      this._sockets.emitLogoutEvent(this.user._id);
    }
    this.user = null;
    this.token = null;
    this._ls.clearAuthToken();
    this._rs.login();
  }

  // TODO REMOVE FUNCTION
  public getUserName() {
    return this.user.name;
  }

  private detectAndSaveCountry(user: any): void {
    fetch('https://ipapi.co/json/')
      .then(r => r.json())
      .then((geo: any) => {
        const detected: string | null = geo?.country_name || null;
        if (!detected) return;
        this.ipCountry = detected;
        user.country = detected;
        // Re-push updated user so localUserCountry is set before any call fires
        this._sockets.setCurrentUser(user);
        // Persist to DB so contacts and socket events carry the country too
        this._apis.userProfileUpdate({ name: user?.name || 'User', country: detected }).subscribe(
          () => console.log('AuthService: country auto-saved to DB:', detected),
          () => {} // silent — geolocation is best-effort
        );
      })
      .catch(() => {});
  }
}
